import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";

const emptyForm = {
  title: "",
  academy_category: "",
  course_model_id: "",
  course_category_id: "",
  course_id: "",
  exam_date: "",
  exam_time: "",
  duration_minutes: 60,
  assignment_mode: "automatic",
  assigned_franchise_ids: [],
};

function NewExamPage() {
  const [meta, setMeta] = useState({ academy_categories: [], models: [], categories: [], courses: [], franchises: [] });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/exams-admin/meta`).then((res) => setMeta(res.data || {})).catch(() => {});
  }, []);

  const models = useMemo(() => (meta.models || []).filter((m) => !form.academy_category || m.academy_category === form.academy_category), [meta.models, form.academy_category]);
  const categories = useMemo(() => (meta.categories || []).filter((c) => !form.academy_category || c.academy_category === form.academy_category), [meta.categories, form.academy_category]);
  const courses = useMemo(
    () => (meta.courses || []).filter((c) => (!form.academy_category || c.academy_category === form.academy_category) && (!form.course_model_id || String(c.course_model_id) === String(form.course_model_id)) && (!form.course_category_id || String(c.course_category_id) === String(form.course_category_id))),
    [meta.courses, form.academy_category, form.course_model_id, form.course_category_id]
  );
  const franchises = useMemo(() => (meta.franchises || []).filter((f) => !form.academy_category || f.academy_category === form.academy_category), [meta.franchises, form.academy_category]);
  const allSelected = franchises.length > 0 && form.assigned_franchise_ids.length === franchises.length;

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const toggleFranchise = (id) => {
    setForm((prev) => {
      const exists = prev.assigned_franchise_ids.includes(id);
      return {
        ...prev,
        assigned_franchise_ids: exists
          ? prev.assigned_franchise_ids.filter((x) => x !== id)
          : [...prev.assigned_franchise_ids, id],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.assignment_mode === "manual" && form.assigned_franchise_ids.length === 0) {
      Swal.fire("Required", "Manual mode e minimum 1 ta franchise select korun.", "warning");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/exams-admin`, {
        ...form,
        course_model_id: Number(form.course_model_id),
        course_category_id: Number(form.course_category_id),
        course_id: Number(form.course_id),
        duration_minutes: Number(form.duration_minutes || 60),
      });
      setForm(emptyForm);
      Swal.fire("Success", "Exam created and assigned.", "success");
    } catch (error) {
      Swal.fire("Failed", error?.response?.data?.message || "Create failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="mb-1">New Exam</h3>
            <p className="text-muted mb-4">Academy, model, category, course select kore exam schedule and assignment set korun.</p>
            <form onSubmit={submit} className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Exam Title</label>
                <input className="form-control" value={form.title} onChange={(e) => setField("title", e.target.value)} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Exam Date</label>
                <input type="date" className="form-control" value={form.exam_date} onChange={(e) => setField("exam_date", e.target.value)} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Exam Time</label>
                <input type="time" className="form-control" value={form.exam_time} onChange={(e) => setField("exam_time", e.target.value)} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Academy Category</label>
                <select className="form-select" value={form.academy_category} onChange={(e) => setForm((p) => ({ ...p, academy_category: e.target.value, course_model_id: "", course_category_id: "", course_id: "", assigned_franchise_ids: [] }))} required>
                  <option value="">Select</option>
                  {(meta.academy_categories || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Course Model</label>
                <select className="form-select" value={form.course_model_id} onChange={(e) => setForm((p) => ({ ...p, course_model_id: e.target.value, course_id: "" }))} required>
                  <option value="">Select</option>
                  {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Course Category</label>
                <select className="form-select" value={form.course_category_id} onChange={(e) => setForm((p) => ({ ...p, course_category_id: e.target.value, course_id: "" }))} required>
                  <option value="">Select</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Course Name</label>
                <select className="form-select" value={form.course_id} onChange={(e) => setField("course_id", e.target.value)} required>
                  <option value="">Select</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Duration (min)</label>
                <input type="number" min="1" className="form-control" value={form.duration_minutes} onChange={(e) => setField("duration_minutes", e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Assignment Mode</label>
                <select className="form-select" value={form.assignment_mode} onChange={(e) => setField("assignment_mode", e.target.value)}>
                  <option value="automatic">Automatic</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              {form.assignment_mode === "manual" ? (
                <div className="col-md-12">
                  <label className="form-label">Assign Franchises (Manual)</label>
                  <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                    <div className="form-check border-bottom pb-2 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="select-all-franchise"
                        checked={allSelected}
                        onChange={(e) =>
                          setField(
                            "assigned_franchise_ids",
                            e.target.checked ? franchises.map((f) => Number(f.id)) : []
                          )
                        }
                      />
                      <label className="form-check-label fw-semibold" htmlFor="select-all-franchise">Select All</label>
                    </div>
                    {franchises.map((f) => {
                      const checked = form.assigned_franchise_ids.includes(Number(f.id));
                      return (
                        <div className="form-check mb-1" key={f.id}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`fr-${f.id}`}
                            checked={checked}
                            onChange={() => toggleFranchise(Number(f.id))}
                          />
                          <label className="form-check-label" htmlFor={`fr-${f.id}`}>
                            {f.centre_name || `Franchise ${f.id}`} ({f.centre_code || "-"}) [{f.status}]
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="col-md-12">
                  <div className="alert alert-info mb-0">Automatic mode e selected academy category er sob franchise e auto assign hobe.</div>
                </div>
              )}
              <div className="col-md-4">
                <button type="submit" className="btn btn-primary w-100" disabled={saving}>{saving ? "Saving..." : "Create Exam"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default NewExamPage;
